import * as React from 'react';
import { Helmet } from 'react-helmet';

import Loader from 'components/atoms/Loader';
import { WithVirtualPageView as WithVirtualPageViewType } from 'components/hoc/withVirtualPageView';
import withVirtualPageView from 'components/hoc/withVirtualPageView';
import { ProgramFilterItem } from 'components/molecules/Program/ProgramFilter';
import ProgramFilter from 'components/molecules/Program/ProgramFilter';
import ProgramHeader from 'components/molecules/Program/ProgramHeader';
import ProgramTeaser from 'components/molecules/Program/ProgramTeaser';
import ProgramOverviewZero from 'components/views/ProgramOverview/fragments/Zero';
import { getBaseTitle, getSeoDataFromViewId } from 'defs/routes';
import i18n from 'helper/i18n';
import noop from 'helper/noop';
import { template } from 'helper/string';
import { ProfileDataType } from 'store/profile';
import { ProgramOverviewItemType } from 'store/programOverview';

export type TeaserType = ProgramOverviewItemType & {
	reactShowFlag: boolean;
};

export type ProgramOverviewProps = WithVirtualPageViewType & {
	id: 'recommended' | 'further';
	currentViewID: string;
	loaded: boolean;
	error?: Object;
	filters: ProgramFilterItem[];
	filterSignature: string;
	recommendedPrograms: TeaserType[];
	allPrograms: TeaserType[];
	loading: boolean;
	userData?: ProfileDataType;
	doLoadOverview: () => void;
};

type ProgramOverviewState = {
	view: string;
};

export const ProgramOverviewFilterValues = {
	RECOMMENDED: 'recommended',
	FURTHER: 'further',
};

export class ProgramOverview extends React.Component<ProgramOverviewProps, ProgramOverviewState> {
	public static displayName = 'ProgramOverview';
	public static defaultProps = {
		loaded: false,
		doLoadPreview: noop,
		error: null,
		loading: false,
		id: '',
		auth: {},
		recommendedPrograms: [],
		allPrograms: [],
	};

	constructor(props: ProgramOverviewProps) {
		super(props);

		this.state = {
			view: props.id || ProgramOverviewFilterValues.RECOMMENDED,
		};
	}

	public componentDidMount() {
		this.props.doLoadOverview();
		this.props.trackVirtualPageView(
			{
				getVirtualPageTitle: this.correspondingSeoTitle,
			},
			500
		);
	}

	public componentDidUpdate(prevProps: ProgramOverviewProps) {
		if (this.props.id !== prevProps.id) {
			this.setState({ view: this.props.id }, () => {
				this.props.trackVirtualPageView(
					{
						getVirtualPageTitle: this.correspondingSeoTitle,
					},
					500
				);
			});
		}
	}

	get teasers() {
		return this.state.view === ProgramOverviewFilterValues.RECOMMENDED
			? this.props.recommendedPrograms
			: this.props.allPrograms;
	}

	get filters() {
		const { recommendedPrograms, allPrograms } = this.props;

		return [
			{
				id: ProgramOverviewFilterValues.RECOMMENDED,
				label: i18n('react.impulsCoach.views.programOverview.filters.recommended'),
				count: recommendedPrograms.length || 0,
			},
			{
				id: ProgramOverviewFilterValues.FURTHER,
				label: i18n('react.impulsCoach.views.programOverview.filters.all'),
				count: allPrograms.length || 0,
			},
		];
	}

	public getTeaserModifierByIndex(teaserIndex: number) {
		const view = this.state.view;
		const smallModifier = 'small';

		switch (view) {
			case ProgramOverviewFilterValues.RECOMMENDED:
				return teaserIndex >= 4 ? smallModifier : void 0; // 1 - 4 should be large
			case ProgramOverviewFilterValues.FURTHER:
				return smallModifier;
			default:
				return void 0;
		}
	}

	get seoData() {
		const { currentViewID } = this.props;
		return getSeoDataFromViewId(currentViewID);
	}

	get correspondingSeoTitle(): string {
		const { view } = this.state;
		let selector: string | null = null;

		switch (view) {
			case ProgramOverviewFilterValues.RECOMMENDED:
				selector = 'recommended';
				break;
			case ProgramOverviewFilterValues.FURTHER:
				selector = 'all';
				break;
			default:
				break;
		}

		return template(
			i18n(
				selector
					? `react.impulsCoach.routes.program.${selector}.title`
					: `react.impulsCoach.routes.program.title`
			),
			{
				count: this.teasers.length,
			}
		);
	}

	public render() {
		const seoData = this.seoData;
		const { loading, allPrograms, id, filterSignature } = this.props;
		const shouldShowLoader = loading && allPrograms.length === 0;

		return (
			<div>
				<Helmet>
					<title>{`${this.correspondingSeoTitle} ${getBaseTitle()}`}</title>
					<meta property="og:title" content={`${seoData.og.title || ''}`} />
				</Helmet>
				<ProgramHeader
					title={i18n('react.impulsCoach.views.programOverview.banner.title')}
					text={i18n('react.impulsCoach.views.programOverview.banner.text')}
				/>
				<div className="container container--full-width-ms">
					<div className="row">
						<div className="col-xs-12">
							<ProgramFilter
								filterSignature={filterSignature}
								selected={id || ProgramOverviewFilterValues.RECOMMENDED}
								filters={this.filters}
							/>
						</div>
						{shouldShowLoader && <Loader />}
						{!shouldShowLoader &&
							this.teasers.map((teaser, index) => {
								return teaser ? (
									<div key={teaser.externalId} className="col-xs-12 col-sm-6 col-md-6">
										<ProgramTeaser
											modifier={this.getTeaserModifierByIndex(index)}
											isNewProgram={teaser.isNewProgram}
											intensity={teaser.intensity}
											duration={teaser.duration}
											title={teaser.title}
											goal={teaser.goal}
											flag={!!teaser.reactShowFlag}
											externalId={teaser.externalId}
											slug={teaser.slug}
											previewImage={teaser.previewImage}
										/>
									</div>
								) : null;
							})}
					</div>
				</div>
				{this.teasers.length === 0 ? <ProgramOverviewZero /> : null}
			</div>
		);
	}
}

export default withVirtualPageView(ProgramOverview as any);
